// =============================================
// src/pages/AgenticAITargetUI.jsx
// =============================================

import React, { useState } from "react";
import { FileText, CheckCircle, Database, FlaskConical, Loader2, Play, Search, Sparkles, X } from "lucide-react";
import { styles } from "../utils/constants";
import { useWorkflow } from "../hooks/useWorkflow";
import ToolsModal from "../components/modals/ToolsModal";
import FeaturesModal from "../components/modals/FeaturesModal";
import LiveModeModal from "../components/modals/LiveModeModal";
import ExpertWorkflowModal from "../components/modals/ExpertWorkflowModal";
import WorkflowStep from "../components/WorkflowStep";
import EmptyState from "../components/EmptyState";
import Header from "../components/Header";


export default function AgenticAITargetUI() {
  const {
    query,
    setQuery,
    loading,
    parsedSteps,
    activeStepIndex,
    executing,
    dataSource,
    isWorkflowComplete,
    runWorkflow,
    reset,
  } = useWorkflow();

  const [showToolsModal, setShowToolsModal] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [workflowRating, setWorkflowRating] = useState(3);
  

  const lift = (e, on) => {
    e.currentTarget.style.transform = on ? "translateY(-2px)" : "translateY(0)";
    e.currentTarget.style.boxShadow = on
      ? "0 12px 28px rgba(2,6,23,0.10)"
      : "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)";
  };


  const executingText = executing ? `Executing step ${activeStepIndex + 1} of ${parsedSteps.length}` : null;

  return (
    <div style={{ minHeight: "100vh" }}>
      <Header isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} setShowUserMenu={setShowUserMenu} showUserMenu={showUserMenu} />

      <div style={styles.container}>
        <div style={{ ...styles.card, transition: "transform .2s ease, box-shadow .2s ease" }} onMouseEnter={(e) => lift(e, true)} onMouseLeave={(e) => lift(e, false)}>
          <div style={{ padding: "28px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: 24,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #0880b8ff 0%, #026597ff 25%, #0369a1 50%, #075985 75%, #0c4a6e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  letterSpacing: "-0.5px",
                  marginTop: 20,
                }}
              >
                <Search size={20} strokeWidth={2} style={{ marginRight: 8, color: "#066595" }} /> Research Query
              </label>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                {(parsedSteps.length > 0 || liveMode || query) && (
                  <button
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: "transparent",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      color: "#3b495bff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => {
                      const ok = window.confirm("Are you sure you want to reset?");
                      if (!ok) return;
                      reset();
                      setLiveMode(false);
                      setShowLiveModal(false);
                      setShowJsonPreview(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                      e.currentTarget.style.borderColor = "#cbd5e1";
                      e.currentTarget.style.color = "#475569";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.color = "#64748b";
                    }}
                  >
                    <X size={14} /> Reset
                  </button>
                )}
              </div>
            </div>

            <textarea
                style={{
                ...styles.queryBox,  // Keep your existing styles
                width: "100%",       // Ensure it takes full width of container
                maxWidth: "100%",    // Prevent overflow
                boxSizing: "border-box",  // Include padding/border in width calculation
                resize: "vertical",  // Allow only vertical resizing
                minHeight: 100,      // Minimum height
                maxHeight: 300,      // Maximum height to prevent too much expansion
                padding: "12px",     // Consistent padding
                fontSize: "14px",
                lineHeight: "1.5",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                outline: "none",
                transition: "border-color 0.2s ease",
                fontFamily: "inherit",
                overflowY: "auto",   // Add scrollbar if content is too long
                wordWrap: "break-word",  // Break long words
                whiteSpace: "pre-wrap"   // Preserve line breaks but wrap text
              }}
              placeholder="Submit your query or select an example below to get started...
              (Note: API retry not yet implemented - please resubmit if request fails)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <div className="button-container" style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: loading || executing ? 0.7 : 1 }} onClick={runWorkflow} disabled={loading || executing}>
                {loading ? (
                  <>
                    <Loader2 size={18} className="spinning" /> Generating Workflow...
                  </>
                ) : executing ? (
                  <>
                    <FlaskConical size={18} /> Executing Workflow...
                  </>
                ) : (
                  <>
                    <Play size={18} /> Generate & Execute Workflow
                  </>
                )}
              </button>

              <button
                style={{ ...styles.button, backgroundColor: liveMode ? "#8BC34A" : "#f59e0b", color: "white", position: "relative" }}
                onClick={() => {
                  if (!liveMode) setShowLiveModal(true);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = liveMode ? "#b6e283ff" : "#d97706";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = liveMode ? "#8BC34A" : "#f59e0b";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {liveMode ? (
                  <>
                    <div style={{ position: "absolute", top: -6, right: -6, width: 12, height: 12, backgroundColor: "#10b981", borderRadius: "50%", border: "2px solid white", animation: "pulse 2s infinite" }} />
                    Live Mode Enabled
                  </>
                ) : (
                  <>
                    <Sparkles size={18} /> Enable Real-Time Monitor
                  </>
                )}
              </button>

              <button
                style={{
                  ...styles.button,
                  ...(isWorkflowComplete
                    ? { background: "linear-gradient(135deg, rgb(44, 62, 80) 50%, rgb(9, 78, 124) 100%)", color: "white" }
                    : { backgroundColor: "#e5e7eb", color: "#9ca3af", border: "1px solid #d1d5db", cursor: "not-allowed", opacity: 0.7, boxShadow: "none" }),
                }}
                disabled={!isWorkflowComplete}
                aria-disabled={!isWorkflowComplete}
                title={isWorkflowComplete ? "Open JSON preview" : "Available after the workflow finishes"}
                onClick={() => isWorkflowComplete && setShowJsonPreview(true)}
                onMouseEnter={(e) => {
                  if (!isWorkflowComplete) return;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (!isWorkflowComplete) return;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <Database size={18} /> Download JSON
              </button>
            
              <button
                style={{
                  ...styles.button,
                  ...(isWorkflowComplete
                    ? { backgroundColor: "#0284c7", color: "white" }
                    : { backgroundColor: "#e5e7eb", color: "#9ca3af", border: "1px solid #d1d5db", cursor: "not-allowed", opacity: 0.7, boxShadow: "none" }),
                }}
                disabled={!isWorkflowComplete}
                title={isWorkflowComplete ? "Rate this workflow" : "Available after the workflow finishes"}
                onClick={() => isWorkflowComplete && setShowRatingModal(true)}
                onMouseEnter={(e) => {
                  if (!isWorkflowComplete) return;
                  e.currentTarget.style.backgroundColor = "#0369a1";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (!isWorkflowComplete) return;
                  e.currentTarget.style.backgroundColor = "#0284c7";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CheckCircle size={18} /> Rate Workflow
              </button>
            </div>


            {!parsedSteps.length && !executing && <EmptyState onPick={(t) => setQuery(t)} />}
          </div>
        </div>

        {parsedSteps.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, padding: "16px 20px", backgroundColor: "#fef3c7", borderRadius: 12, border: "1px solid #fbbf24" }}>
              <Sparkles size={20} color="#92400e" />
              <span style={{ fontSize: 15, fontWeight: 600, color: "#92400e" }}>Workflow Generated: {parsedSteps.length} Steps</span>
              {dataSource && (
                <span style={{ marginLeft: "auto", fontSize: 13, padding: "4px 8px", borderRadius: 4, color: dataSource === "openai" ? "#15803d" : "#6b7280", fontWeight: 500 }}>
                  {dataSource === "openai" ? "AlzTXPilot" : "System Error"}
                </span>
              )}
              {executing && <span style={{ fontSize: 14, color: "#78350f" }}>{executingText}</span>}
              {liveMode && !executing && (
                <span style={{ fontSize: 13, color: "#059669", backgroundColor: "#dcfce7", padding: "4px 10px", borderRadius: 12, display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                  <div style={{ width: 8, height: 8, backgroundColor: "#10b981", borderRadius: "50%", animation: "pulse 2s infinite" }} />
                  Live Mode Active
                </span>
              )}
            </div>

            {parsedSteps.map((step, index) => (
              <WorkflowStep key={index} step={step} index={index} isActive={index <= activeStepIndex} />
            ))}
          </div>
        )}

        <ToolsModal open={showToolsModal} onClose={() => setShowToolsModal(false)} />
        <FeaturesModal open={featuresModalOpen} onClose={() => setFeaturesModalOpen(false)} />
        <LiveModeModal
          open={showLiveModal}
          onClose={() => setShowLiveModal(false)}
          onConfirm={() => {
            setLiveMode(true);
            setShowLiveModal(false);
          }}
        />
       
        {/* Rating Modal */}
        {showRatingModal && (
          <>
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)", zIndex: 999 }} onClick={() => setShowRatingModal(false)} />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                borderRadius: 16,
                padding: 28,
                maxWidth: 400,
                width: "90%",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                zIndex: 1000,
                border: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: 20, fontWeight: 700 }}>
                Rate This Workflow
              </h3>
              <p style={{ color: "#64748b", fontSize: 14, marginBottom: 24 }}>
                Your rating helps train the system for better results
              </p>
              
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 14, color: "#475569" }}>Rating</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#0284c7" }}>{workflowRating}/5</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={workflowRating}
                  onChange={(e) => setWorkflowRating(e.target.value)}
                  style={{
                    width: "100%",
                    height: 8,
                    background: `linear-gradient(to right, #0284c7 0%, #0284c7 ${(workflowRating - 1) * 25}%, #e2e8f0 ${(workflowRating - 1) * 25}%, #e2e8f0 100%)`,
                    borderRadius: 4,
                    outline: "none",
                    cursor: "pointer",
                    WebkitAppearance: "none",
                    appearance: "none"
                  }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>Poor</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>Excellent</span>
                </div>
              </div>
              
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  style={{ ...styles.button, padding: "10px 20px", fontSize: 14, backgroundColor: "transparent", color: "#64748b", border: "2px solid #e2e8f0" }}
                  onClick={() => setShowRatingModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{ ...styles.button, padding: "10px 20px", fontSize: 14, backgroundColor: "#0284c7", color: "white" }}
                  onClick={() => {
                    console.log(`Rating submitted: ${workflowRating}`);
                    setShowRatingModal(false);
                    // Add your rating submission logic here
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#0369a1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#0284c7";
                  }}
                >
                  Submit Rating
                </button>
              </div>
            </div>
          </>
        )}

        {showJsonPreview && (
          <>
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)", zIndex: 999 }} onClick={() => setShowJsonPreview(false)} />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                borderRadius: 16,
                padding: 28,
                maxWidth: 800,
                width: "90%",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                zIndex: 1000,
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: 20, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                  <Database size={20} color="#6366f1" /> Workflow JSON Export
                </h3>
                <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>Download a JSON snapshot of the latest workflow execution</p>
              </div>
              <div style={{ flex: 1, backgroundColor: "#1e293b", borderRadius: 8, padding: 20, overflow: "auto", marginBottom: 20, border: "1px solid #334155" }}>
                <pre style={{ margin: 0, color: "#e2e8f0", fontSize: 12, fontFamily: "Monaco, Consolas, monospace", lineHeight: 1.6 }}>
                  {JSON.stringify(
                    {
                      metadata: {
                        platform: "AlzTxPILOT",
                        version: "prototype",
                        generated: new Date().toISOString(),
                        query: query,
                        dataSource: dataSource,
                        disclaimer: "PROTOTYPE - Demonstration purposes only",
                      },
                      workflow: {
                        totalSteps: parsedSteps.length,
                        steps: parsedSteps.map((step, index) => ({
                          stepNumber: index + 1,
                          title: step.title,
                          tool: step.tool,
                          description: step.description,
                          input: step.input,
                          output: step.output,
                          status: index <= activeStepIndex ? "completed" : "pending",
                        })),
                      },
                      tools_used: [...new Set(parsedSteps.map((s) => s.tool))],
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  style={{ ...styles.button, padding: "10px 20px", fontSize: 14, backgroundColor: "transparent", color: "#64748b", border: "2px solid #e2e8f0" }}
                  onClick={() => setShowJsonPreview(false)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  Close
                </button>
                <button
                  style={{ ...styles.button, padding: "10px 20px", fontSize: 14, backgroundColor: "#6366f1", color: "white", border: "none", opacity: 0.5, cursor: "not-allowed" }}
                  disabled
                  title="Download functionality coming soon"
                >
                  <Database size={16} /> Download (Coming Soon)
                </button>
              </div>
            </div>
          </>
        )}

        {/* Expert Workflow Review Section - Shows when workflow is complete */}
        {isWorkflowComplete && parsedSteps.length > 0 && (
          <ExpertWorkflowModal 
            workflowData={{
              metadata: {
                platform: "AlzTxPILOT",
                version: "prototype",
                generated: new Date().toISOString(),
                query: query,
                dataSource: dataSource,
                disclaimer: "PROTOTYPE - Demonstration purposes only",
              },
              workflow: {
                totalSteps: parsedSteps.length,
                steps: parsedSteps.map((step, index) => ({
                  stepNumber: index + 1,
                  title: step.title,
                  tool: step.tool,
                  description: step.description,
                  input: step.input,
                  output: step.output,
                  status: index <= activeStepIndex ? "completed" : "pending",
                })),
              },
              tools_used: [...new Set(parsedSteps.map((s) => s.tool))],
            }}
          />
        )}

        {/* Bottom two-column section: yellow preview + two buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: '48px auto', maxWidth: 1000 }}>
          {/* Left: Yellow preview box */}
          <div
            style={{
              flex: '1 1 400px',
              minWidth: 0,
              padding: '20px 28px',
              borderRadius: 16,
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fffbeb 100%)',
              border: '1px solid rgba(217,119,6,0.2)',
              boxShadow: '0 10px 25px rgba(251,191,36,0.08), 0 2px 8px rgba(251,191,36,0.06)',
              height: 'fit-content',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'start', gap: 12 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 3px 8px rgba(245,158,11,0.3)',
                }}
              >
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>!</span>
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#d97706',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 4,
                  }}
                >
                  AlzTxPILOT: Development Preview
                </div>
                <p style={{ fontSize: 14, color: '#78350f', lineHeight: 1.6, margin: 0 }}>
                  An AI agentic planner that iteratively tests hypotheses across multimodal Alzheimer's disease data,
                  tools, and literature. Live UI and reasoning trace with model-backed stand-ins for unavailable integrations.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Buttons */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              flex: '1 1 400px',
              minWidth: 0,
              padding: '28px 28px',
              height: 'fit-content',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
              <button
                style={{
                  background: '#fff',
                  color: '#0284c7',
                  border: '2px solid #0284c7',
                  borderRadius: 10,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all .3s ease',
                }}
                onClick={() => setShowToolsModal(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f9ff';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,165,233,.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Database size={14} /> Browse Tools and Databases
              </button>

              <button
                style={{
                  background: '#fff',
                  color: '#0284c7',
                  border: '2px solid #0284c7',
                  borderRadius: 10,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all .3s ease',
                }}
                onClick={() => setFeaturesModalOpen(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f9ff';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,165,233,.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Sparkles size={14} /> Feature Highlights
              </button>
            </div>
          </div>
        </div>

        

       
      </div>

      <footer style={{ marginTop: 80, borderTop: "2px solid #e2e8f0", padding: 40 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 40, marginBottom: 32 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>AlzTxPILOT</span>
              <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>Links coming soon</span>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: 0 }}>
              Precision Integration of Literature and Omics to Targets & Drugs for Alzheimer Disease. Powered by multi-modal AI and comprehensive AD databases.
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 16 }}>Resources</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>Documentation</a>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>API Access</a>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>Publications</a>
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 16 }}>Contact</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>Support</a>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>Partnerships</a>
              <a href="#" style={{ fontSize: 13, color: "#64748b", textDecoration: "none" }}>Feedback</a>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 0 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>© {new Date().getFullYear()} AlzTxPILOT. All rights reserved.</p>
          <div style={{ display: "flex", gap: 24 }}>
            <a href="#" style={{ fontSize: 12, color: "#94a3b8", textDecoration: "none" }}>Terms</a>
            <a href="#" style={{ fontSize: 12, color: "#94a3b8", textDecoration: "none" }}>Privacy</a>
            <a href="#" style={{ fontSize: 12, color: "#94a3b8", textDecoration: "none" }}>Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

